
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/auth/AuthContext';
import { supabase } from '../integrations/supabase/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

interface ProfileData {
  full_name: string;
  email: string;
  teacher_id?: string;
}

const Profile = () => {
  const { user, loading: authLoading } = useAuth();
  const [profileData, setProfileData] = useState<ProfileData>({
    full_name: '',
    email: '',
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const { toast } = useToast();

  // Redirect if not logged in
  if (!authLoading && !user) {
    return <Navigate to="/auth" replace />;
  }

  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        try {
          // First set basic user data from auth
          setProfileData({
            full_name: user.user_metadata?.full_name || '',
            email: user.email || '',
          });
          
          // Then fetch additional profile data from the profiles table
          const { data, error } = await supabase
            .from('profiles')
            .select('teacher_id')
            .eq('email', user.email)
            .single();
          
          if (error) {
            console.error('Error fetching profile:', error);
          } else if (data) {
            // Update profile data with teacher_id from the database
            setProfileData(prevData => ({
              ...prevData,
              teacher_id: data.teacher_id || '',
            }));
          }
        } catch (error) {
          console.error('Error processing profile data:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchProfile();
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    try {
      setUpdating(true);
      
      // Update user metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: { full_name: profileData.full_name }
      });
      
      if (authError) throw authError;
      
      // Update profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          full_name: profileData.full_name
          // We don't update teacher_id here since it's read-only
        })
        .eq('email', user.email);
        
      if (profileError) throw profileError;
      
      toast({
        title: 'อัปเดตโปรไฟล์สำเร็จ',
        description: 'ข้อมูลของคุณได้รับการอัปเดตเรียบร้อยแล้ว',
      });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error?.message || 'ไม่สามารถอัปเดตโปรไฟล์ได้',
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-form-blue" />
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto px-4 py-8">
      <div className="max-w-md mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>โปรไฟล์</CardTitle>
            <CardDescription>จัดการข้อมูลส่วนตัวของคุณ</CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">ชื่อ-นามสกุล</Label>
                <Input
                  id="fullName"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">อีเมล</Label>
                <Input
                  id="email"
                  value={profileData.email}
                  disabled
                  className="bg-gray-50"
                />
                <p className="text-xs text-muted-foreground">คุณไม่สามารถเปลี่ยนแปลงอีเมลได้</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="teacherId">รหัสครู</Label>
                <Input
                  id="teacherId"
                  value={profileData.teacher_id || ''}
                  readOnly
                  className="bg-gray-50"
                  placeholder="รหัสครูของคุณ"
                />
                <p className="text-xs text-muted-foreground">รหัสครูจะถูกอัปเดตอัตโนมัติเมื่อคุณส่งแบบฟอร์ม</p>
              </div>
            </CardContent>
            <CardFooter>
              <Button type="submit" className="w-full" disabled={updating}>
                {updating ? 'กำลังอัปเดต...' : 'บันทึกการเปลี่ยนแปลง'}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
